import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useToast } from "@/components/flexkit/toast";
import { useGetPreferenceByKey, useSetPreference } from "@/queries/user-preferences-queries";
import { formatError } from "@/utils/errors";
import { isValidURL } from "@/utils/url-validation";

/**
 * useContextURL is a hook that provides a context URL from the hash of the current location
 *
 * @returns contextURL: the URL from the hash of the current location
 */
export function useContextURL(initialContextURL: string) {
    const location = useLocation();
    const navigate = useNavigate();

    const [contextURL, setContextURL] = useState<string | undefined>();
    const [contextURLValid, setContextURLValid] = useState<boolean>(false);

    const updateContextURL = useCallback((newUrl: string) => {
        setContextURL(newUrl);
        try {
            setContextURLValid(new URL(newUrl.toLocaleLowerCase()).pathname.length > 3);
        } catch {
            setContextURLValid(false);
        }
    }, []);

    const isValidURLCallback = useCallback((url: string): boolean => isValidURL(url), []);
    // Handle initial URL
    useEffect(() => {
        if (initialContextURL && isValidURLCallback(initialContextURL)) {
            updateContextURL(initialContextURL);
        }
    }, [initialContextURL, isValidURLCallback, updateContextURL]);
    useEffect(() => {
        const newUrl = (location.hash || "#").slice(1).trim();
        if (!contextURL && isValidURLCallback(newUrl)) {
            updateContextURL(newUrl);
            navigate({ hash: "" }); // after we initialize the contextURL, we can clean up
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        contextURL,
        setContextURL: updateContextURL,
        contextURLValid,
    };
}

export const CONTEXT_URLS = "CONTEXT_URLS";

export function useListContextUrls() {
    const { data: preference, isPending: isPendingPreferences } = useGetPreferenceByKey(CONTEXT_URLS);

    let contextUrls: string[] | undefined;
    const preferenceValue = preference?.value;
    if (preferenceValue) {
        try {
            contextUrls = JSON.parse(preferenceValue) as string[];
        } catch (e) {
            console.error(`Failed to parse ${CONTEXT_URLS} preference`, e);
        }
    }

    return {
        contextUrls,
        isPending: isPendingPreferences,
    };
}

export function useSetContextUrls() {
    const { toast } = useToast();
    const setPreference = useSetPreference();

    return useCallback(
        async (contextUrls: string[]) => {
            try {
                const maxSize = 1024 * 10; // 10KiB limit
                const urls: string[] = [];

                // Try to add URLs one by one until we hit the size limit
                for (const url of contextUrls) {
                    urls.push(url);
                    const currentValue = JSON.stringify(urls);

                    if (currentValue.length > maxSize) {
                        // Remove the last URL that caused us to exceed the limit
                        urls.pop();
                        break;
                    }
                }

                const value = JSON.stringify(urls);
                await setPreference.mutateAsync({ key: CONTEXT_URLS, value });
            } catch (e) {
                toast({
                    title: "Failed to update context urls",
                    description: formatError(e),
                });
            }
        },
        [setPreference, toast],
    );
}
